'use client';

import { useState } from 'react';
import Image from 'next/image';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FaLinkedin } from 'react-icons/fa';
import { asText, GroupField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { Content } from '@prismicio/client';
import { asLink } from '@prismicio/helpers';
import { Simplify } from '@/prismicio-types';

interface TeamMembersGridProps {
  members: GroupField<Simplify<Content.AboutDocumentDataTeamMembersItem>> | null;
  viewButtonText: string;
}

type TeamMember = Simplify<Content.AboutDocumentDataTeamMembersItem>;

export default function TeamMembersGrid({ members, viewButtonText }: TeamMembersGridProps) {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  return (
    <div className="py-8">
      {members && members.length > 0 ? (
        <div className="fade-up grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {members.map((member, index) => (
            <div
              key={index}
              className="relative z-10 overflow-hidden rounded-lg bg-white shadow-md"
            >
              {member.image.url && (
                <Image
                  src={member.image.url}
                  alt={member.image.alt ?? ''}
                  width={600}
                  height={600}
                  quality={100}
                  className="h-64 w-full object-cover"
                />
              )}
              <div className="p-4 text-left">
                <h3 className="text-lg font-semibold">
                  <PrismicRichText field={member.name} />
                </h3>
                <p className="text-sm text-gray-400">{member.position}</p>
                <div className="mt-6 flex items-center justify-between">
                  {member.linkedin_url && asLink(member.linkedin_url) && (
                    <a
                      href={asLink(member.linkedin_url) ?? '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn Profile"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <FaLinkedin className="h-6 w-6" />
                    </a>
                  )}

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        className={`text-xs font-bold uppercase ${asText(member.biography).length > 20 ? 'visible' : 'hidden'} `}
                        variant="outline"
                        onClick={() => setSelectedMember(member)}
                      >
                        {viewButtonText}
                      </Button>
                    </DialogTrigger>
                    <DialogContent closeButtonColor="#000000" className="p-10">
                      <DialogHeader>
                        <DialogTitle>
                          <div className="text-gradient-red text-2xl">
                            <PrismicRichText field={selectedMember?.name} />
                          </div>
                        </DialogTitle>
                      </DialogHeader>
                      {selectedMember?.biography && (
                        <PrismicRichText
                          components={{
                            em: ({ children }) => (
                              <em className="mt-2 block border-t border-gray-100 pt-4 text-sm text-gray-400">
                                {children}
                              </em>
                            ),
                          }}
                          field={selectedMember.biography}
                        />
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>No team members to display.</p>
      )}
    </div>
  );
}
